/**
 * Which product the Creator selects when it opens.
 *
 * Three URL shapes used to be answered by three separate effects, each gating on
 * one shared "already decided" ref. That made every new entry path an extra
 * clause in three places, which is the multi-guard fragility recorded in
 * `docs/solutions/ui-bugs/wrong-product-mockup-edit-placement.md`.
 *
 * This module answers the question once, as a pure function. The caller applies
 * the result; nothing here touches a store.
 */

import type { Product, ProductColor } from '@/api/products';
import type { GuidedPreselection } from '@/stores/guidedEntryStore';

/** Product name used when the URL names nothing. */
export const DEFAULT_PRODUCT_NAME = 'Organic Oversize Shirt';
/**
 * The sync id of that product. `name` is the Shop Language's translation now,
 * so a French shop would not find the default by name — the id is the key,
 * the name only a fallback for catalogues without sync ids.
 */
export const DEFAULT_PRODUCT_EXTERNAL_ID = 621;
/** Colour name used when the URL names nothing. */
export const DEFAULT_COLOR_NAME = 'black';
/** The supplier's hex for that colour; names are translated, hex codes are not. */
export const DEFAULT_COLOR_HEX = '#272727';

export type InitialSelectionSource = 'product' | 'sku' | 'default';

export interface InitialSelectionInput {
  /** `?product=` — an explicit product id. */
  productIdFromUrl: string | null;
  /** `?color=` — only meaningful together with `?product=`. */
  colorIdFromUrl: string | null;
  /** `?SKU=` / `?sku=` — an article number. */
  skuFromUrl: string | null;
  /** Catalogue, empty while still loading. */
  products: Product[] | undefined;
  /** False when an existing design is being edited rather than created. */
  isFreshCreatorVisit: boolean;
}

export type InitialSelection =
  /** Catalogue not loaded yet — ask again once it is. */
  | { status: 'wait' }
  /** Nothing to select, and nothing to report. */
  | { status: 'skip' }
  /** The URL named something that does not exist. Decided: do not fall back. */
  | { status: 'error'; message: string }
  | {
      status: 'select';
      source: InitialSelectionSource;
      product: Product;
      colorId: string | null;
      /**
       * True when every other product must be deactivated first. Only an
       * explicit `?product=` is exclusive — it means "show exactly this".
       */
      exclusive: boolean;
    };

function byName(value: string | undefined, target: string): boolean {
  return value?.toLowerCase() === target.toLowerCase();
}

/** Colour for an explicit `?product=`: the requested one, else the first. */
function colorForProductParam(
  product: Product,
  colorIdFromUrl: string | null
): ProductColor | undefined {
  const requested = colorIdFromUrl
    ? product.colors?.find((c) => c.id === colorIdFromUrl)
    : undefined;
  return requested ?? product.colors?.[0];
}

/** Colour for a `?SKU=`: heathered wins, because that is the catalogue default. */
function colorForSku(product: Product): ProductColor | undefined {
  return product.colors?.find((c) => c.isHeathered) ?? product.colors?.[0];
}

/** Colour for the default product: the named one, else the first. */
function colorForDefault(product: Product): ProductColor | undefined {
  return (
    product.colors?.find(
      (c) =>
        c.hexCode?.toLowerCase() === DEFAULT_COLOR_HEX ||
        byName(c.name, DEFAULT_COLOR_NAME)
    ) ?? product.colors?.[0]
  );
}

/**
 * Decide the Creator's initial selection.
 *
 * Precedence is `?product=`, then `?SKU=`, then the default product. A named
 * but unresolvable product or SKU ends in `error` rather than quietly falling
 * through to the default — the seller asked for something specific, and
 * silently opening a different product would be worse than saying so.
 */
export function resolveInitialSelection(
  input: InitialSelectionInput
): InitialSelection {
  const { productIdFromUrl, colorIdFromUrl, skuFromUrl, products } = input;

  const named = Boolean(productIdFromUrl || skuFromUrl);
  if (!named && !input.isFreshCreatorVisit) {
    return { status: 'skip' };
  }

  if (!products || products.length === 0) {
    return { status: 'wait' };
  }

  if (productIdFromUrl) {
    const product = products.find((p) => p.id === productIdFromUrl);
    if (!product) {
      return {
        status: 'error',
        message: 'Product from URL was not found in catalog',
      };
    }
    return {
      status: 'select',
      source: 'product',
      product,
      colorId:
        colorForProductParam(product, colorIdFromUrl)?.id ??
        colorIdFromUrl ??
        null,
      exclusive: true,
    };
  }

  if (skuFromUrl) {
    const product = products.find((p) => p.artNumber === skuFromUrl);
    if (!product) {
      return {
        status: 'error',
        message: `No product found for SKU "${skuFromUrl}"`,
      };
    }
    return {
      status: 'select',
      source: 'sku',
      product,
      colorId: colorForSku(product)?.id ?? null,
      exclusive: false,
    };
  }

  const product =
    products.find(
      (p) =>
        p.externalId === DEFAULT_PRODUCT_EXTERNAL_ID ||
        byName(p.name, DEFAULT_PRODUCT_NAME)
    ) ?? products[0];

  if (!product) {
    return { status: 'skip' };
  }

  return {
    status: 'select',
    source: 'default',
    product,
    colorId: colorForDefault(product)?.id ?? null,
    exclusive: false,
  };
}

/**
 * `?SKU=` or `?sku=`. The param was introduced as `SKU`, but embed links in
 * the wild use `sku` and a case mismatch silently selected nothing.
 */
export function skuFromSearchParams(params: URLSearchParams): string | null {
  return params.get('SKU') || params.get('sku') || null;
}

/**
 * Whether the URL names a product for the initial selection at all. Read
 * before the catalogue has loaded, to know whether a preselection is coming.
 */
export function namesInitialProduct(params: URLSearchParams): boolean {
  return Boolean(params.get('product') || skuFromSearchParams(params));
}

/**
 * What the guided entry records about the initial selection. A product named
 * in the URL is recorded with its source; the fallback default product and an
 * unresolvable name both end as "no product". `wait` and `skip` are not an
 * answer yet, so nothing is recorded for them.
 */
export function guidedPreselectionFrom(
  selection: InitialSelection
): GuidedPreselection | undefined {
  if (selection.status === 'error') return { productId: null };
  if (selection.status !== 'select') return undefined;
  if (selection.source === 'default') return { productId: null };
  return { productId: selection.product.id, source: selection.source };
}
